// ../../../node_modules/.pnpm/@effect+sql-sqlite-wasm@4.0.0-rc.108_@effect+wa-sqlite@0.1.2_effect@4.0.0-rc.108/node_modules/@effect/sql-sqlite-wasm/dist/SqliteClient.js
import * as WaSqlite from "@dxos/wa-sqlite";
import SQLiteESMFactory from "@dxos/wa-sqlite/dist/wa-sqlite.mjs";
import { MemoryVFS } from "@dxos/wa-sqlite/src/examples/MemoryVFS.js";
import * as Config from "effect/Config";
import * as Context from "effect/Context";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Fiber from "effect/Fiber";
import { identity } from "effect/Function";
import * as Layer from "effect/Layer";
import * as Rec from "effect/Record";
import * as Scope from "effect/Scope";
import * as ScopedRef from "effect/ScopedRef";
import * as Semaphore from "effect/Semaphore";
import * as Stream from "effect/Stream";
import * as Reactivity from "effect/unstable/reactivity/Reactivity";
import * as Client from "effect/unstable/sql/SqlClient";
import { classifySqliteError, SqlError } from "effect/unstable/sql/SqlError";
import * as Statement from "effect/unstable/sql/Statement";
var ATTR_DB_SYSTEM_NAME = "db.system.name";
var classifyError = (cause, message, operation) => classifySqliteError(cause, {
  message,
  operation
});
var TypeId = "~@effect/sql-sqlite-wasm/SqliteClient";
var SqliteClient = /* @__PURE__ */ Context.Service("@effect/sql-sqlite-wasm/SqliteClient");
var initModule = /* @__PURE__ */ Effect.runSync(/* @__PURE__ */ Effect.cached(/* @__PURE__ */ Effect.promise(() => SQLiteESMFactory())));
var initEffect = /* @__PURE__ */ Effect.runSync(/* @__PURE__ */ Effect.cached(/* @__PURE__ */ initModule.pipe(/* @__PURE__ */ Effect.map((module) => WaSqlite.Factory(module)))));
var registered = /* @__PURE__ */ new Set();
var makeMemory = (options) => Effect.gen(function* () {
  const reactivity = yield* Reactivity.Reactivity;
  const compiler = Statement.makeCompilerSqlite(options.transformQueryNames);
  const transformRows = options.transformResultNames ? Statement.defaultTransforms(options.transformResultNames).array : void 0;
  const makeConnection = Effect.gen(function* () {
    const sqlite3 = yield* initEffect;
    if (registered.has("memory-vfs") === false) {
      registered.add("memory-vfs");
      const module = yield* initModule;
      const vfs = new MemoryVFS("memory-vfs", module);
      sqlite3.vfs_register(vfs, false);
    }
    const db = yield* Effect.acquireRelease(Effect.try({
      try: () => sqlite3.open_v2(":memory:", void 0, "memory-vfs"),
      catch: (cause) => new SqlError({
        reason: classifyError(cause, "Failed to open database", "openDatabase")
      })
    }), (db2) => Effect.sync(() => sqlite3.close(db2)));
    if (options.installReactivityHooks) {
      sqlite3.update_hook(db, (_op, _db, table, rowid) => {
        if (!table) return;
        const id = String(Number(rowid));
        reactivity.invalidateUnsafe({
          [table]: [id]
        });
      });
    }
    const run = (sql, params = [], rowMode = "object") => Effect.try({
      try: () => {
        const results = [];
        for (const stmt of sqlite3.statements(db, sql)) {
          let columns;
          sqlite3.bind_collection(stmt, params);
          while (sqlite3.step(stmt) === WaSqlite.SQLITE_ROW) {
            columns = columns ?? sqlite3.column_names(stmt);
            const row = sqlite3.row(stmt);
            if (rowMode === "object") {
              const obj = {};
              for (let i = 0; i < columns.length; i++) {
                Rec.assignProperty(obj, columns[i], row[i]);
              }
              results.push(obj);
            } else {
              results.push(row);
            }
          }
        }
        return results;
      },
      catch: (cause) => new SqlError({
        reason: classifyError(cause, "Failed to execute statement", "execute")
      })
    });
    return identity({
      execute(sql, params, transformRows2) {
        return transformRows2 ? Effect.map(run(sql, params), transformRows2) : run(sql, params);
      },
      executeRaw(sql, params) {
        return run(sql, params);
      },
      executeValues(sql, params) {
        return run(sql, params, "array");
      },
      executeValuesUnprepared(sql, params) {
        return run(sql, params, "array");
      },
      executeUnprepared(sql, params, transformRows2) {
        return this.execute(sql, params, transformRows2);
      },
      executeStream(sql, params, transformRows2) {
        function* stream() {
          for (const stmt of sqlite3.statements(db, sql)) {
            let columns;
            sqlite3.bind_collection(stmt, params);
            while (sqlite3.step(stmt) === WaSqlite.SQLITE_ROW) {
              columns = columns ?? sqlite3.column_names(stmt);
              const row = sqlite3.row(stmt);
              const obj = {};
              for (let i = 0; i < columns.length; i++) {
                Rec.assignProperty(obj, columns[i], row[i]);
              }
              yield obj;
            }
          }
        }
        return Stream.suspend(() => Stream.fromIteratorSucceed(stream()[Symbol.iterator]())).pipe(transformRows2 ? Stream.mapArray((chunk) => transformRows2(chunk)) : identity, Stream.mapError((cause) => new SqlError({
          reason: classifyError(cause, "Failed to execute statement", "stream")
        })));
      },
      export: Effect.try({
        try: () => sqlite3.serialize(db, "main"),
        catch: (cause) => new SqlError({
          reason: classifyError(cause, "Failed to export database", "export")
        })
      }),
      import(data) {
        return Effect.try({
          try: () => sqlite3.deserialize(db, "main", data, data.length, data.length, 1 | 2),
          catch: (cause) => new SqlError({
            reason: classifyError(cause, "Failed to import database", "import")
          })
        });
      }
    });
  });
  const semaphore = yield* Semaphore.make(1);
  const connection = yield* makeConnection;
  const acquirer = semaphore.withPermits(1)(Effect.succeed(connection));
  const transactionAcquirer = Effect.uninterruptibleMask((restore) => {
    const fiber = Fiber.getCurrent();
    const scope2 = Context.getUnsafe(fiber.context, Scope.Scope);
    return Effect.as(Effect.tap(restore(semaphore.take(1)), () => Scope.addFinalizer(scope2, semaphore.release(1))), connection);
  });
  return Object.assign(yield* Client.make({
    acquirer,
    compiler,
    transactionAcquirer,
    spanAttributes: [...options.spanAttributes ? Object.entries(options.spanAttributes) : [], [ATTR_DB_SYSTEM_NAME, "sqlite"]],
    transformRows
  }), {
    [TypeId]: TypeId,
    config: options,
    export: semaphore.withPermits(1)(connection.export),
    import(data) {
      return semaphore.withPermits(1)(connection.import(data));
    }
  });
});
var make5 = (options) => Effect.gen(function* () {
  const reactivity = yield* Reactivity.Reactivity;
  const compiler = Statement.makeCompilerSqlite(options.transformQueryNames);
  const transformRows = options.transformResultNames ? Statement.defaultTransforms(options.transformResultNames).array : void 0;
  const makeConnection = Effect.gen(function* () {
    let currentId = 0;
    const pending = /* @__PURE__ */ new Map();
    const scope2 = yield* Effect.scope;
    const readyDeferred = yield* Deferred.make();
    const worker = yield* options.worker;
    const port = "port" in worker ? worker.port : worker;
    const postMessage = (message, transferables) => port.postMessage(message, transferables);
    yield* Scope.addFinalizer(scope2, Effect.sync(() => postMessage(["close"])));
    const onMessage = (event) => {
      const [id, error, results] = event.data;
      if (id === "ready") {
        Deferred.doneUnsafe(readyDeferred, Exit.void);
        return;
      } else if (id === "update_hook") {
        reactivity.invalidateUnsafe({
          [error]: [results]
        });
        return;
      } else {
        const resume = pending.get(id);
        if (!resume) return;
        pending.delete(id);
        if (error) {
          resume(Exit.fail(new SqlError({
            reason: classifyError(error, "Failed to execute statement", "execute")
          })));
        } else {
          resume(Exit.succeed(results));
        }
      }
    };
    port.addEventListener("message", onMessage);
    function onError(cause) {
      const exit = Exit.fail(new SqlError({
        reason: classifyError(cause, "SQLite WASM worker failed", "worker")
      }));
      const requests = Array.from(pending.values());
      pending.clear();
      for (const resume of requests) {
        resume(exit);
      }
      Effect.runFork(ScopedRef.set(connectionRef, makeConnection));
    }
    if ("onerror" in worker) {
      worker.addEventListener("error", onError);
    }
    yield* Scope.addFinalizer(scope2, Effect.sync(() => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
    }));
    yield* Deferred.await(readyDeferred);
    if (options.installReactivityHooks) {
      postMessage(["update_hook"]);
    }
    const send = (id, message, transferables) => Effect.callback((resume) => {
      pending.set(id, resume);
      postMessage(message, transferables);
    });
    const run = (sql, params = [], rowMode = "object") => {
      const rows = Effect.withFiber((fiber) => {
        const id = currentId++;
        return send(id, [id, sql, params], fiber.getRef(Transferables));
      });
      return rowMode === "object" ? Effect.map(rows, extractObject) : Effect.map(rows, extractRows);
    };
    return identity({
      execute(sql, params, transformRows2) {
        return transformRows2 ? Effect.map(run(sql, params), transformRows2) : run(sql, params);
      },
      executeRaw(sql, params) {
        return run(sql, params);
      },
      executeValues(sql, params) {
        return run(sql, params, "array");
      },
      executeValuesUnprepared(sql, params) {
        return run(sql, params, "array");
      },
      executeUnprepared(sql, params, transformRows2) {
        return this.execute(sql, params, transformRows2);
      },
      executeStream() {
        return Stream.die("executeStream not implemented");
      },
      export: Effect.suspend(() => {
        const id = currentId++;
        return send(id, ["export", id]);
      }),
      import(data) {
        return Effect.suspend(() => {
          const id = currentId++;
          return send(id, ["import", id, data], [data.buffer]);
        });
      }
    });
  });
  const connectionRef = yield* ScopedRef.fromAcquire(makeConnection);
  const semaphore = yield* Semaphore.make(1);
  const acquirer = semaphore.withPermits(1)(ScopedRef.get(connectionRef));
  const transactionAcquirer = Effect.uninterruptibleMask(Effect.fnUntraced(function* (restore) {
    const fiber = Fiber.getCurrent();
    const scope2 = Context.getUnsafe(fiber.context, Scope.Scope);
    yield* restore(semaphore.take(1));
    yield* Scope.addFinalizer(scope2, semaphore.release(1));
    return yield* ScopedRef.get(connectionRef);
  }));
  return Object.assign(yield* Client.make({
    acquirer,
    compiler,
    transactionAcquirer,
    spanAttributes: [...options.spanAttributes ? Object.entries(options.spanAttributes) : [], [ATTR_DB_SYSTEM_NAME, "sqlite"]],
    transformRows
  }), {
    [TypeId]: TypeId,
    config: options,
    export: Effect.flatMap(acquirer, (connection) => connection.export),
    import(data) {
      return Effect.flatMap(acquirer, (connection) => connection.import(data));
    }
  });
});
function rowToObject(columns, row) {
  const obj = {};
  for (let i = 0; i < columns.length; i++) {
    Rec.assignProperty(obj, columns[i], row[i]);
  }
  return obj;
}
var extractObject = (rows) => rows[1].map((row) => rowToObject(rows[0], row));
var extractRows = (rows) => rows[1];
var Transferables = /* @__PURE__ */ Context.Reference("@effect/sql-sqlite-wasm/currentTransferables", {
  defaultValue: () => []
});
var withTransferables = (transferables) => (effect) => Effect.provideService(effect, Transferables, transferables);
var layerMemoryConfig = (config) => Layer.effectContext(Config.unwrap(config).pipe(Effect.flatMap(makeMemory), Effect.map((client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client))))).pipe(Layer.provide(Reactivity.layer));
var layerMemory = (config) => Layer.effectContext(Effect.map(makeMemory(config), (client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client)))).pipe(Layer.provide(Reactivity.layer));
var layer2 = (config) => Layer.effectContext(Effect.map(make5(config), (client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client)))).pipe(Layer.provide(Reactivity.layer));
var layerConfig = (config) => Layer.effectContext(Config.unwrap(config).pipe(Effect.flatMap(make5), Effect.map((client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client))))).pipe(Layer.provide(Reactivity.layer));

export {
  TypeId,
  SqliteClient,
  makeMemory,
  make5 as make,
  Transferables,
  withTransferables,
  layerMemoryConfig,
  layerMemory,
  layer2 as layer,
  layerConfig
};
//# sourceMappingURL=chunk-FLW6R5Z2.mjs.map
